import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import {
  BookOpen, ArrowDown, CheckCircle, Heart, Sparkles,
  Music, Flame, Download, Truck, Leaf, Gift,
} from "lucide-react";

/* ───────────────────────────────────────────────────────
   THE BOOK OF COLORZ — Phase I (Paperback First)
   Slimmed-down release visual · "Where Colorz Unite Us"
   Vision reference lives at /book-of-colox (untouched)
   ─────────────────────────────────────────────────────── */

const ASSETS = {
  heroCollage: "https://customer-assets-wrfwihn1.emergentagent.net/job_d1bfd6a0-9e0a-4e12-8a4e-a1799be5ed79/artifacts/u1nrsaiw_Book%20of%20Colorz%20Cover-%20Full%20Edition%20website%20background%20image.webp",
  coverMockup: "/book-of-colorz-cover.png",
  cathedral: "https://images.unsplash.com/photo-1637572177881-8650be30a539?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxjYXRoZWRyYWwlMjBsaWdodCUyMHJheXMlMjBzdGFpbmVkJTIwZ2xhc3MlMjBkYXJrfGVufDB8fHx8MTc3MjAzNTIxM3ww&ixlib=rb-4.1.0&q=85",
  seedPdf: "/downloads/seedz-shy-yahffirmations.pdf",
};

// Single Phase-1 product. Price kept on the 22–33 sacred scale (easy to adjust).
const PAPERBACK = {
  packageId: "paperback",
  price: "$22.22",
  meaning: "An experience, no matter the medium",
  items: [
    "90 sacred pages, print-on-demand paperback",
    "38 “Seedz to Free Yah Mind” coloring designs",
    "22 Yahffirmations woven throughout",
    "Premium paper — made to color, made to keep",
    "Ships to your door · Free Seedz preview included",
  ],
};

// Pricing (mirrors backend SHIPPING_TIERS). Book price EXCLUDES shipping.
// Phased launch: public checkout stays hidden until this flips true (or ?billing=test).
const BILLING_ENABLED_DEFAULT = false;

const BOOK_PRICE = 22.22;
const FREE_SHIP_THRESHOLD = 88.88;

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } },
};
const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.15 } } };
const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6 } },
};

function AnimSection({ children, className = "", variants = fadeUp }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} variants={variants} initial="hidden" animate={inView ? "visible" : "hidden"} className={className}>
      {children}
    </motion.div>
  );
}

const BookOfColorz = () => {
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [seedzName, setSeedzName] = useState("");
  const [seedzEmail, setSeedzEmail] = useState("");
  const [seedzSubmitting, setSeedzSubmitting] = useState(false);
  const [seedzDone, setSeedzDone] = useState(false);
  const [seedzError, setSeedzError] = useState("");

  // Checkout modal — Stripe collects address/phone/shipping/tax; we keep a light cart + optional email.
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [checkoutStep, setCheckoutStep] = useState("cart");
  const [form, setForm] = useState({ customer_name: "", customer_email: "" });
  // Unite Local scheduled pickup
  const [fulfillMethod, setFulfillMethod] = useState("ship"); // ship | pickup
  const [pickupWindows, setPickupWindows] = useState([]);
  const [pickupLocation, setPickupLocation] = useState("");
  const [selectedWindowId, setSelectedWindowId] = useState("");
  const [pickupHold, setPickupHold] = useState(null); // {hold_id, expires_at, window_label, location}
  const [holdSecondsLeft, setHoldSecondsLeft] = useState(0);
  const [pickupLoading, setPickupLoading] = useState(false);
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Live price from Stripe (dashboard-driven); BOOK_PRICE is only a fallback.
  const [bookPrice, setBookPrice] = useState(BOOK_PRICE);
  useEffect(() => {
    fetch('/api/payments/packages')
      .then((r) => r.json())
      .then((d) => { const a = d?.packages?.paperback?.amount; if (a) setBookPrice(Number(a)); })
      .catch(() => {});
  }, []);


  // ── PHASED LAUNCH — billing OFF for the public until go-live. ──
  // Checkout flow stays fully built & testable: append ?billing=test to the URL
  // to unlock the purchase pills (for QA / the testing environment).
  const [billingEnabled] = useState(() => {
    if (BILLING_ENABLED_DEFAULT) return true;
    try {
      const qs = new URLSearchParams(window.location.search).get("billing") === "test";
      const path = window.location.pathname.includes("book-of-colorz-test");
      return qs || path;
    } catch { return false; }
  });
  const scrollToSeedz = () => document.getElementById("seedz")?.scrollIntoView({ behavior: "smooth" });
  const SOON_LABEL = "Coming soon — get the free Seedz while you wait";

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const fetchPickupWindows = async () => {
    setPickupLoading(true);
    try {
      const res = await fetch("/api/payments/pickup/windows");
      const data = await res.json();
      setPickupWindows(data.windows || []);
      setPickupLocation(data.location || "");
    } catch (e) {
      console.error("pickup windows error", e);
    } finally {
      setPickupLoading(false);
    }
  };

  useEffect(() => {
    if (showCheckout && fulfillMethod === "pickup") fetchPickupWindows();
  }, [showCheckout, fulfillMethod]);

  const releasePickupHold = async () => {
    if (!pickupHold?.hold_id) return;
    const hid = pickupHold.hold_id;
    setPickupHold(null);
    setHoldSecondsLeft(0);
    try {
      await fetch("/api/payments/pickup/release", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hold_id: hid }),
      });
    } catch (e) { /* best-effort */ }
  };

  const startPickupHold = async () => {
    setCheckoutError("");
    if (!selectedWindowId) { setCheckoutError("Please choose a pickup time."); return; }
    setPickupLoading(true);
    try {
      const res = await fetch("/api/payments/pickup/hold", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ window_id: selectedWindowId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setCheckoutError(typeof err.detail === "string" ? err.detail : "That time just filled up — please choose another.");
        fetchPickupWindows();
        return;
      }
      const data = await res.json();
      setPickupHold(data);
      setCheckoutStep("details");
    } catch (e) {
      setCheckoutError("Could not reserve your spot. Please try again.");
    } finally {
      setPickupLoading(false);
    }
  };

  // 15-min hold countdown (concert-ticket style)
  useEffect(() => {
    if (!pickupHold?.expires_at) return;
    const tick = () => {
      const left = Math.max(0, Math.round((new Date(pickupHold.expires_at).getTime() - Date.now()) / 1000));
      setHoldSecondsLeft(left);
      if (left <= 0) {
        setPickupHold(null);
        setCheckoutStep("cart");
        setCheckoutError("Your held pickup spot expired — please choose a time again.");
        fetchPickupWindows();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [pickupHold]);

  const closeCheckout = () => {
    releasePickupHold();
    setShowCheckout(false);
  };

  const handleCheckout = async () => {
    setCheckoutError("");
    const isPickup = fulfillMethod === "pickup";
    if (isPickup) {
      if (!form.customer_name.trim()) { setCheckoutError("Please enter your name for pickup."); return; }
      if (!form.customer_email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email)) {
        setCheckoutError("Please enter a valid email for your pickup confirmation."); return;
      }
      if (!pickupHold?.hold_id) { setCheckoutError("Your pickup hold expired — please choose a time again."); setCheckoutStep("cart"); return; }
    } else if (form.customer_email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.customer_email)) {
      // Email is optional for ship (Stripe also collects it); validate only when provided.
      setCheckoutError("Please enter a valid email address.");
      return;
    }
    setCheckoutLoading(true);
    try {
      const response = await fetch('/api/payments/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: PAPERBACK.packageId,
          origin_url: window.location.origin,
          cancel_path: '/book-of-colorz',
          quantity,
          fulfillment_channel: isPickup ? "LOCAL" : "SHIP",
          pickup_hold_id: isPickup ? pickupHold.hold_id : undefined,
          customer_name: form.customer_name.trim() || undefined,
          customer_email: form.customer_email.trim() || undefined,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const detail = err.detail;
        throw new Error(typeof detail === "string" ? detail : "Checkout failed");
      }
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      setCheckoutError(typeof error.message === "string" ? error.message : "Unable to start checkout. Please try again.");
      console.error('Checkout error:', error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSeedzSubmit = async (e) => {
    e.preventDefault();
    setSeedzError("");
    if (!seedzName.trim() || !seedzEmail.trim()) {
      setSeedzError("Please share your name and email.");
      return;
    }
    setSeedzSubmitting(true);
    try {
      const response = await fetch('/api/subscribe/seedz', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: seedzName.trim(), email: seedzEmail.trim() }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong');
      }
      const data = await response.json();
      setSeedzDone(true);
    } catch (error) {
      setSeedzError("We couldn't send your Seedz just now. Please try again.");
      console.error('Seedz subscribe error:', error);
    } finally {
      setSeedzSubmitting(false);
    }
  };

  return (
    <div
      data-testid="book-of-colorz-page"
      className="min-h-screen"
      style={{ background: "#0a0a0f", fontFamily: "'Cormorant Garamond', Georgia, serif" }}
    >
      {billingEnabled && !BILLING_ENABLED_DEFAULT && (
        <div
          data-testid="billing-test-badge"
          className="colox-sans"
          style={{ position: "fixed", bottom: 16, left: 16, zIndex: 200, background: "linear-gradient(135deg,#7a3b2e,#b0553f)", color: "#fff", fontSize: 11, letterSpacing: "0.12em", padding: "8px 14px", borderRadius: 999, boxShadow: "0 6px 24px rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.25)" }}
        >
          🧪 TEST MODE — billing unlocked for QA (not the public page)
        </div>
      )}
      {showCheckout && (() => {
        const subtotal = bookPrice * quantity;
        const money = (v) => `$${v.toFixed(2)}`;
        return (
        <div
          data-testid="checkout-modal"
          className="fixed inset-0 z-[100] overflow-y-auto"
          style={{ background: "rgba(5,4,10,0.88)", backdropFilter: "blur(6px)" }}
        >
          <div
            className="flex min-h-full items-start sm:items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) closeCheckout(); }}
          >
            <div
              className="colox-card rounded-2xl w-full max-w-lg my-4 flex flex-col overflow-hidden"
              style={{ border: "1px solid rgba(212,162,84,0.25)", background: "#100d18", maxHeight: "88vh" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 sm:px-8 pt-6 pb-4" style={{ borderBottom: "1px solid rgba(212,162,84,0.12)" }}>
                <div className="flex items-center gap-3">
                  {checkoutStep === "details" && (
                    <button
                      data-testid="checkout-back-btn"
                      onClick={() => { setCheckoutStep("cart"); setCheckoutError(""); releasePickupHold(); }}
                      className="colox-sans text-sm"
                      style={{ color: "rgba(201,184,150,0.7)" }}
                    >←</button>
                  )}
                  <h3 className="colox-serif text-2xl" style={{ color: "#e8c87a" }}>
                    {checkoutStep === "cart" ? "My Cart" : "Checkout"}
                  </h3>
                </div>
                <button
                  data-testid="checkout-close-btn"
                  onClick={closeCheckout}
                  className="colox-sans text-lg"
                  style={{ color: "rgba(201,184,150,0.6)" }}
                >✕</button>
              </div>

              {/* Body (scrolls) */}
              <div className="overflow-y-auto px-6 sm:px-8 py-5" style={{ flex: 1 }}>
                {checkoutStep === "cart" ? (
                  <div data-testid="cart-step">
                    <div className="flex items-start justify-between gap-4 pb-4" style={{ borderBottom: "1px solid rgba(212,162,84,0.12)" }}>
                      <div className="flex-1">
                        <p className="colox-serif text-lg leading-tight" style={{ color: "#e8c87a" }}>The Book of Colorz</p>
                        <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.6)" }}>Yahffirmations Shy — INSPIYAH Edition</p>
                        <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.5)" }}>Paperback · Print-on-Demand · {money(bookPrice)} each</p>
                        <div className="flex items-center gap-3 mt-3">
                          <button data-testid="qty-decrease-btn" onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="colox-sans w-8 h-8 rounded-full" style={{ border: "1px solid rgba(212,162,84,0.35)", color: "#e8c87a" }}>−</button>
                          <span data-testid="qty-value" className="colox-serif text-lg w-6 text-center" style={{ color: "#e8c87a" }}>{quantity}</span>
                          <button data-testid="qty-increase-btn" onClick={() => setQuantity((q) => Math.min(20, q + 1))} className="colox-sans w-8 h-8 rounded-full" style={{ border: "1px solid rgba(212,162,84,0.35)", color: "#e8c87a" }}>+</button>
                        </div>
                      </div>
                      <span className="colox-sans text-sm" style={{ color: "#e8c87a", whiteSpace: "nowrap" }}>{money(subtotal)}</span>
                    </div>

                    {/* Delivery method */}
                    <div className="mt-5">
                      <p className="colox-sans text-xs mb-2" style={{ color: "rgba(201,184,150,0.6)", letterSpacing: "1px" }}>DELIVERY METHOD</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          data-testid="method-ship-btn"
                          onClick={() => { setFulfillMethod("ship"); releasePickupHold(); setCheckoutError(""); }}
                          className="colox-sans py-3 rounded-lg text-sm"
                          style={{ border: `1px solid ${fulfillMethod === "ship" ? "#d4a254" : "rgba(212,162,84,0.25)"}`, background: fulfillMethod === "ship" ? "rgba(212,162,84,0.12)" : "transparent", color: fulfillMethod === "ship" ? "#e8c87a" : "rgba(201,184,150,0.8)" }}
                        >Ship to me</button>
                        <button
                          data-testid="method-pickup-btn"
                          onClick={() => { setFulfillMethod("pickup"); setCheckoutError(""); }}
                          className="colox-sans py-3 rounded-lg text-sm"
                          style={{ border: `1px solid ${fulfillMethod === "pickup" ? "#d4a254" : "rgba(212,162,84,0.25)"}`, background: fulfillMethod === "pickup" ? "rgba(212,162,84,0.12)" : "transparent", color: fulfillMethod === "pickup" ? "#e8c87a" : "rgba(201,184,150,0.8)" }}
                        >Local Pickup · Unite Local</button>
                      </div>
                    </div>

                    {fulfillMethod === "ship" ? (
                      <p className="colox-sans text-xs mt-4" style={{ color: "rgba(201,184,150,0.5)" }}>Shipping address, delivery speed &amp; tax are chosen securely on the next step.</p>
                    ) : (
                      <div className="mt-4" data-testid="pickup-window-picker">
                        <p className="colox-sans text-xs mb-2" style={{ color: "rgba(201,184,150,0.6)", letterSpacing: "1px" }}>CHOOSE A PICKUP TIME</p>
                        {pickupLoading && pickupWindows.length === 0 ? (
                          <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.5)" }}>Loading times…</p>
                        ) : (
                          <div className="space-y-2">
                            {pickupWindows.map((w) => (
                              <button
                                key={w.window_id}
                                data-testid={`pickup-window-${w.window_id}`}
                                disabled={w.sold_out}
                                onClick={() => { setSelectedWindowId(w.window_id); setCheckoutError(""); }}
                                className="w-full flex items-center justify-between px-4 py-3 rounded-lg colox-sans text-sm text-left"
                                style={{ border: `1px solid ${selectedWindowId === w.window_id ? "#d4a254" : "rgba(212,162,84,0.25)"}`, background: selectedWindowId === w.window_id ? "rgba(212,162,84,0.12)" : "transparent", opacity: w.sold_out ? 0.4 : 1, cursor: w.sold_out ? "not-allowed" : "pointer", color: "#e8c87a" }}
                              >
                                <span>{w.label}</span>
                                <span className="colox-sans text-xs" style={{ color: w.sold_out ? "#e07a5f" : "#8fbf8f", whiteSpace: "nowrap" }}>{w.sold_out ? "Full" : `${w.remaining} left`}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {pickupLocation && (
                          <p className="colox-sans text-xs mt-3" style={{ color: "rgba(201,184,150,0.55)" }} data-testid="pickup-location">📍 {pickupLocation}</p>
                        )}
                        <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.45)" }}>Your spot is held for 15 minutes while you check out.</p>
                        {checkoutError && (<p data-testid="pickup-error" className="colox-sans text-sm mt-3" style={{ color: "#e07a5f" }}>{checkoutError}</p>)}
                      </div>
                    )}
                  </div>
                ) : (
                  <div data-testid="details-step">
                    {fulfillMethod === "pickup" ? (
                      <>
                        <div className="mb-4 p-4 rounded-lg" style={{ border: "1px solid rgba(212,162,84,0.3)", background: "rgba(212,162,84,0.06)" }}>
                          <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.6)", letterSpacing: "1px" }}>YOUR PICKUP</p>
                          <p className="colox-serif text-lg mt-1" style={{ color: "#e8c87a" }} data-testid="pickup-selected-window">{pickupHold?.window_label || "—"}</p>
                          <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.6)" }}>📍 {pickupHold?.location || pickupLocation}</p>
                          <p className="colox-sans text-xs mt-3" data-testid="pickup-hold-timer" style={{ color: holdSecondsLeft <= 120 ? "#e07a5f" : "#8fbf8f" }}>
                            ⏳ Spot held — {mmss(holdSecondsLeft)} remaining
                          </p>
                        </div>
                        <p className="colox-sans text-sm mb-3 leading-relaxed" style={{ color: "rgba(201,184,150,0.85)" }}>
                          We need your <strong style={{ color: "#e8c87a", fontWeight: 500 }}>name</strong> and <strong style={{ color: "#e8c87a", fontWeight: 500 }}>email</strong> to confirm your pickup (no shipping address needed).
                        </p>
                        <div className="space-y-3">
                          <input data-testid="checkout-name" value={form.customer_name} onChange={setField("customer_name")} placeholder="Full name (required)" className="colox-input" />
                          <input data-testid="checkout-email" type="email" value={form.customer_email} onChange={setField("customer_email")} placeholder="Email for your pickup confirmation (required)" className="colox-input" />
                        </div>
                        <div className="mt-5 p-4 rounded-lg" style={{ border: "1px solid rgba(212,162,84,0.18)", background: "rgba(212,162,84,0.05)" }}>
                          <p className="colox-sans text-xs leading-relaxed" style={{ color: "rgba(201,184,150,0.78)" }}>
                            <span style={{ color: "rgba(212,162,84,0.9)", fontWeight: 500 }}>Local pickup</span> — bring your order # to the window above. Can't make it? Reply to reschedule; unclaimed copies are held 7 days.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="colox-sans text-sm mb-4 leading-relaxed" style={{ color: "rgba(201,184,150,0.85)" }}>
                          Your <strong style={{ color: "#e8c87a", fontWeight: 500 }}>shipping address</strong>, <strong style={{ color: "#e8c87a", fontWeight: 500 }}>delivery speed</strong> and payment are collected securely on the next step by <strong style={{ color: "#e8c87a", fontWeight: 500 }}>Stripe</strong>.
                        </p>
                        <div className="space-y-3">
                          <input data-testid="checkout-name" value={form.customer_name} onChange={setField("customer_name")} placeholder="Full name (optional)" className="colox-input" />
                          <input data-testid="checkout-email" type="email" value={form.customer_email} onChange={setField("customer_email")} placeholder="Email for your receipt & order tracking (optional)" className="colox-input" />
                        </div>
                        <div className="mt-5 p-4 rounded-lg" style={{ border: "1px solid rgba(212,162,84,0.18)", background: "rgba(212,162,84,0.05)" }}>
                          <p className="colox-sans text-xs leading-relaxed" style={{ color: "rgba(201,184,150,0.78)" }}>
                            <span style={{ color: "rgba(212,162,84,0.9)", fontWeight: 500 }}>Print-on-demand</span> — each book is lovingly printed for you, then shipped. Standard delivery estimate is about 10–14 days.
                          </p>
                        </div>
                      </>
                    )}
                    {checkoutError && (<p data-testid="checkout-error" className="colox-sans text-sm mt-4" style={{ color: "#e07a5f" }}>{checkoutError}</p>)}
                  </div>
                )}
              </div>

              {/* Footer (fixed) */}
              <div className="px-6 sm:px-8 py-4" style={{ borderTop: "1px solid rgba(212,162,84,0.15)", background: "#0d0b13" }}>
                <div className="space-y-1.5 mb-4">
                  <div className="flex justify-between colox-sans text-sm" style={{ color: "rgba(201,184,150,0.7)" }}>
                    <span>Subtotal ({quantity} × {money(bookPrice)})</span><span>{money(subtotal)}</span>
                  </div>
                  <div className="flex justify-between colox-sans text-sm" style={{ color: "rgba(201,184,150,0.7)" }}>
                    <span>{fulfillMethod === "pickup" ? "Local Pickup" : "Shipping & tax"}</span>
                    {fulfillMethod === "pickup" ? (
                      <span style={{ color: "#8fbf8f" }}>FREE</span>
                    ) : (
                      <span style={{ color: "rgba(201,184,150,0.6)" }}>Calculated at checkout</span>
                    )}
                  </div>
                  {fulfillMethod === "ship" && FREE_SHIP_THRESHOLD > 0 && (
                    subtotal >= FREE_SHIP_THRESHOLD ? (
                      <p data-testid="free-ship-hint" className="colox-sans text-xs pt-0.5" style={{ color: "#8fbf8f" }}>✓ You've unlocked FREE shipping</p>
                    ) : (
                      <p data-testid="free-ship-hint" className="colox-sans text-xs pt-0.5" style={{ color: "rgba(201,184,150,0.55)" }}>Add {money(FREE_SHIP_THRESHOLD - subtotal)} more for FREE shipping</p>
                    )
                  )}
                  <div className="flex justify-between colox-serif text-lg pt-1" style={{ color: "#e8c87a" }}>
                    <span>Subtotal</span><span data-testid="checkout-total">{money(subtotal)}</span>
                  </div>
                </div>

                {checkoutStep === "cart" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <button data-testid="continue-shopping-btn" onClick={closeCheckout} className="colox-sans py-3.5 rounded-full text-sm" style={{ border: "1px solid rgba(212,162,84,0.35)", color: "rgba(201,184,150,0.85)" }}>Continue Shopping</button>
                    <button
                      data-testid="cart-checkout-btn"
                      disabled={pickupLoading}
                      onClick={() => {
                        setCheckoutError("");
                        if (fulfillMethod === "pickup") { startPickupHold(); }
                        else { setCheckoutStep("details"); }
                      }}
                      className="colox-btn-gold py-3.5 rounded-full text-sm"
                      style={{ opacity: pickupLoading ? 0.6 : 1 }}
                    >{fulfillMethod === "pickup" ? (pickupLoading ? "Reserving…" : "Reserve Spot →") : "Checkout →"}</button>
                  </div>
                ) : (
                  <>
                    <p data-testid="checkout-consent" className="colox-sans text-xs text-center mb-3 leading-relaxed" style={{ color: "rgba(201,184,150,0.85)" }}>
                      By continuing, you agree to our <a href="/book-of-colorz/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: "#d4a254" }} data-testid="consent-terms-link">Terms</a>, <a href="/book-of-colorz/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#d4a254" }} data-testid="consent-privacy-link">Privacy Policy</a>, and <a href="/book-of-colorz/terms-of-service#refunds" target="_blank" rel="noopener noreferrer" style={{ color: "#d4a254" }} data-testid="consent-refund-link">Refund Policy</a>.
                    </p>
                    <button data-testid="checkout-submit-btn" onClick={() => handleCheckout()} disabled={checkoutLoading} className="colox-btn-gold w-full py-4 rounded-full text-sm" style={{ opacity: checkoutLoading ? 0.6 : 1 }}>
                      {checkoutLoading ? "Preparing secure checkout..." : (fulfillMethod === "pickup" ? "PAY & CONFIRM PICKUP →" : "CONTINUE TO SECURE PAYMENT →")}
                    </button>
                  </>
                )}
                <p className="colox-sans text-xs text-center mt-3" style={{ color: "rgba(201,184,150,0.5)" }}>Secure checkout via Stripe · {fulfillMethod === "pickup" ? "Local pickup · Unite Local" : "Print-on-demand"}</p>
              </div>
            </div>
          </div>
        </div>
        );
      })()}


      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Raleway:wght@300;400;500;600&display=swap');
        .colox-serif { font-family: 'Cormorant Garamond', Georgia, serif; }
        .colox-sans { font-family: 'Raleway', 'Segoe UI', sans-serif; }
        .colox-glow { box-shadow: 0 0 60px rgba(212,162,84,0.15), 0 0 120px rgba(212,162,84,0.05); }
        .colox-text-glow { text-shadow: 0 0 40px rgba(212,162,84,0.3); }
        .colox-divider { background: linear-gradient(90deg, transparent, rgba(212,162,84,0.4), transparent); height: 1px; }
        .colox-card { background: linear-gradient(160deg, rgba(26,21,37,0.9), rgba(15,12,20,0.95)); border: 1px solid rgba(212,162,84,0.12); transition: all 0.4s ease; }
        .colox-input { width: 100%; background: #0a0a0f; border: 1px solid rgba(212,162,84,0.18); border-radius: 10px; padding: 12px 14px; color: #f5efe6; font-family: 'Raleway','Segoe UI',sans-serif; font-size: 14px; outline: none; transition: border-color 0.25s ease; }
        .colox-input::placeholder { color: rgba(201,184,150,0.62); }
        .colox-input:focus { border-color: rgba(212,162,84,0.55); }
        .colox-btn-gold { background: linear-gradient(135deg, #d4a254, #e8c87a); color: #0a0a0f; font-family: 'Raleway', sans-serif; font-weight: 600; letter-spacing: 0.05em; transition: all 0.3s ease; }
        .colox-btn-gold:hover { background: linear-gradient(135deg, #e8c87a, #f5d78e); box-shadow: 0 4px 24px rgba(212,162,84,0.3); transform: translateY(-1px); }
        .colox-btn-outline { border: 1px solid rgba(212,162,84,0.3); color: #d4a254; font-family: 'Raleway', sans-serif; font-weight: 500; letter-spacing: 0.05em; transition: all 0.3s ease; background: transparent; }
        .colox-btn-outline:hover { border-color: rgba(212,162,84,0.6); background: rgba(212,162,84,0.08); }
        @keyframes gentleFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes softPulse { 0%,100% { opacity: 0.4; } 50% { opacity: 0.8; } }
        .animate-float { animation: gentleFloat 6s ease-in-out infinite; }
        .animate-soft-pulse { animation: softPulse 4s ease-in-out infinite; }
      `}</style>

      {/* ── ANNOUNCEMENT ── */}
      {showAnnouncement && (
        <div
          data-testid="announcement-bar"
          className="relative colox-sans text-center py-2.5 px-4"
          style={{ background: "linear-gradient(90deg, #1a1525, #1e1830, #1a1525)", borderBottom: "1px solid rgba(212,162,84,0.15)" }}
        >
          <p className="text-xs tracking-widest" style={{ color: "#d4a254" }}>
            SACRED LAUNCH &mdash; WHERE COLORZ UNITE US! &middot; A NATION'S PRAYER &middot; COMEDY HOUR COMES WITH EVERY ORDER
          </p>
          <button
            onClick={() => setShowAnnouncement(false)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-xs opacity-40 hover:opacity-80"
            style={{ color: "#d4a254" }}
          >
            &times;
          </button>
        </div>
      )}

      {/* ── NAV ── */}
      <nav
        data-testid="colorz-nav"
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{ background: "rgba(10,10,15,0.88)", borderBottom: "1px solid rgba(212,162,84,0.08)" }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Flame className="w-5 h-5" style={{ color: "#d4a254" }} />
            <span className="colox-serif text-lg font-semibold tracking-wider" style={{ color: "#e8c87a" }}>
              THE BOOK OF COLORZ
            </span>
          </div>
          <div className="hidden md:flex items-center gap-8 colox-sans text-xs tracking-widest">
            {[
              { label: "THE BOOK", href: "#the-book" },
              { label: "THE PAPERBACK", href: "#paperback" },
              { label: "FREE SEEDZ", href: "#seedz" },
              { label: "CREATOR'S NOTE", href: "#creator-note" },
              { label: "OUR STORY", href: "#our-story" },
            ].map((item) => (
              <a key={item.label} href={item.href} className="hover:opacity-100 transition-opacity" style={{ color: "rgba(212,162,84,0.6)" }}>
                {item.label}
              </a>
            ))}
          </div>
          <a href={billingEnabled ? "#paperback" : "#seedz"}>
            <button data-testid="nav-cta-button" className="colox-btn-outline px-4 py-2 rounded-full text-xs">
              {billingEnabled ? "GET YOUR 1ST INSPIYAH EDITION" : "COMING SOON · FREE SEEDZ"}
            </button>
          </a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section data-testid="hero-section" className="relative overflow-hidden" style={{ minHeight: "100vh" }}>
        <div className="absolute inset-0" style={{ transform: `translateY(${scrollY * 0.3}px)` }}>
          <img src={ASSETS.heroCollage} alt="The Book of Colorz — Full Edition cover art" className="w-full h-full object-cover" style={{ opacity: 0.62, filter: "saturate(0.95) brightness(0.62)" }} />
        </div>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,10,15,0.3) 0%, rgba(10,10,15,0.6) 40%, rgba(10,10,15,0.95) 85%, #0a0a0f 100%)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% -20%, rgba(212,162,84,0.1) 0%, transparent 60%)" }} />

        <div className="relative z-10 max-w-5xl mx-auto px-6 flex flex-col items-center justify-center" style={{ minHeight: "100vh" }}>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.2, ease: "easeOut" }} className="text-center">
            <p className="colox-sans text-xs tracking-[0.35em] mb-6" style={{ color: "rgba(212,162,84,0.6)" }}>
              BY KEEPER OF THE FIYAH
            </p>
            <h1 className="colox-serif text-5xl sm:text-6xl lg:text-8xl font-light leading-tight mb-4 colox-text-glow" style={{ color: "#f5efe6" }}>
              The Book of{" "}
              <span className="font-semibold italic" style={{ color: "#d4a254" }}>Colorz</span>
            </h1>
            <div className="colox-divider w-48 mx-auto my-6" />
            <p className="colox-serif text-xl sm:text-2xl italic font-light mb-3" style={{ color: "#c9b896" }}>
              "Divine dark feminine, led by the light"
            </p>
            <p className="colox-sans text-sm max-w-xl mx-auto mb-10 leading-relaxed" style={{ color: "rgba(201,184,150,0.6)" }}>
              A sacred coloring experience for the woman who walks in faith and dares to
              discover new inspiration. Each page is a prayer. Each color, a revelation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a href={billingEnabled ? "#paperback" : "#seedz"}>
                <button data-testid="hero-shop-btn" className="colox-btn-gold px-8 py-3 rounded-full text-sm">
                  {billingEnabled ? "GET YOUR 1ST INSPIYAH EDITION" : "COMING SOON — GET THE FREE SEEDZ"}
                </button>
              </a>
              <a href="#seedz">
                <button className="colox-btn-outline px-8 py-3 rounded-full text-sm">
                  FREE SEEDZ
                </button>
              </a>
            </div>
            <motion.div className="mt-16" animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.5 }}>
              <ArrowDown className="w-5 h-5 mx-auto" style={{ color: "rgba(212,162,84,0.3)" }} />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SCRIPTURE INTERLUDE ── */}
      <section className="py-20 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-3xl mx-auto text-center">
          <Flame className="w-6 h-6 mx-auto mb-6 animate-soft-pulse" style={{ color: "#d4a254" }} />
          <blockquote className="colox-serif text-2xl sm:text-3xl italic font-light leading-relaxed mb-4" style={{ color: "#c9b896" }}>
            "Place me as a seal upon your heart... for love is as strong as death,
            its flames are a blazing fire, the flame of YAH."
          </blockquote>
          <p className="colox-sans text-xs tracking-widest" style={{ color: "rgba(212,162,84,0.5)" }}>
            SONG OF SOLOMON 8:6
          </p>
        </AnimSection>
      </section>

      {/* ── ABOUT THE BOOK ── */}
      <section id="the-book" className="py-24 px-6 relative" style={{ background: "linear-gradient(to bottom, #0a0a0f, #0f0c14, #0a0a0f)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(212,162,84,0.04) 0%, transparent 50%)" }} />
        <div className="relative z-10 max-w-6xl mx-auto">
          <AnimSection className="text-center mb-16">
            <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>WHAT AWAITS WITHIN</p>
            <h2 className="colox-serif text-4xl sm:text-5xl font-light" style={{ color: "#f5efe6" }}>More Than A Coloring Book, It's A YAHFFIRMATION!</h2>
          </AnimSection>
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <AnimSection>
              <div className="relative">
                <div className="colox-glow rounded-2xl overflow-hidden">
                  <img src="/assets/yah-spirit-boc.png" alt="YAHFFIRMATIONS — Yah Spirit, Book of Colorz" className="w-full rounded-2xl" style={{ objectFit: "contain", width: "100%" }} />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full animate-float" style={{ background: "radial-gradient(circle, rgba(212,162,84,0.15), transparent 70%)" }} />
              </div>
            </AnimSection>
            <AnimSection>
              <div className="space-y-6">
                <p className="colox-serif text-xl italic" style={{ color: "#d4a254" }}>
                  "She is Her, She is You, Executing as the BUTTERFLY"
                </p>
                <p className="colox-sans text-sm leading-relaxed" style={{ color: "rgba(201,184,150,0.7)" }}>
                  The Book of Colorz is not simply pages to fill with color. It is a sacred
                  invitation &mdash; for the woman who has walked faithfully and now feels a
                  stirring for something deeper. Each illustration is a doorway. Each
                  affirmation, a whisper from the divine.
                </p>
                <p className="colox-sans text-sm leading-relaxed" style={{ color: "rgba(201,184,150,0.7)" }}>
                  Rooted in scripture, inspired by the divine dark feminine energy that has
                  always existed within creation, this book invites you to color your way
                  into a new conversation with the Almighty. You don't have to leave your
                  faith. You simply open a new room within it.
                </p>
                <div className="space-y-3 pt-4">
                  {[
                    { icon: BookOpen, text: "38 hand-curated sacred designs" },
                    { icon: Heart, text: "22 YAHFFIRMATIONS woven throughout" },
                    { icon: Music, text: "Scripture reflections & meditations" },
                    { icon: Sparkles, text: "A spiritual practice, not just art" },
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <f.icon className="w-4 h-4 flex-shrink-0" style={{ color: "#d4a254" }} />
                      <span className="colox-sans text-sm" style={{ color: "#c9b896" }}>{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ── 7 SACRED SECTIONS ── */}
      <section className="py-20 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-4xl mx-auto">
          <p className="colox-sans text-xs tracking-[0.3em] text-center mb-10" style={{ color: "rgba(212,162,84,0.5)" }}>7 SACRED SECTIONS INSIDE</p>
          <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-40px" }} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "SHY, YOU", verse: "Isaiah 43:10" },
              { title: "FLAME", verse: "Song of Solomon 8:6" },
              { title: "REMEMBER THE SHY", verse: "2 Kings 20:3" },
              { title: "EYE TOUCH LOVE", verse: "1 John 4:8" },
              { title: "TRIBE TO INSPIYAH", verse: "Revelation 5:5" },
              { title: "YOD HEY VAU HEY", verse: "Yahuwah · Isaiah 43:11,12" },
              { title: "YHWH יהוה IS THE WAY", verse: "Isaiah 38:16" },
              { title: "BONUS", verse: "Dedication" },
            ].map((s, i) => (
              <motion.div key={i} variants={scaleIn} className="colox-card rounded-xl p-4 text-center">
                <p className="colox-serif text-sm font-semibold" style={{ color: "#e8c87a" }}>{s.title}</p>
                <p className="colox-sans text-xs mt-1" style={{ color: "rgba(212,162,84,0.4)" }}>{s.verse}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimSection>
      </section>

      {/* ── COLORING EXPERIENCE GUIDE LINK ── */}
      <section className="pb-20 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-3xl mx-auto">
          <Link
            to="/coloring-guide"
            data-testid="coloring-guide-link"
            className="colox-card rounded-2xl p-8 flex flex-col sm:flex-row items-center justify-between gap-5 group"
            style={{ textDecoration: "none" }}
          >
            <div className="flex items-center gap-4 text-center sm:text-left">
              <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(212,162,84,0.18), rgba(232,200,122,0.08))", border: "1px solid rgba(212,162,84,0.3)" }}>
                <Leaf className="w-5 h-5" style={{ color: "#e8c87a" }} />
              </div>
              <div>
                <h3 className="colox-serif text-2xl font-light" style={{ color: "#f5efe6" }}>The Coloring Experience Guide</h3>
                <p className="colox-sans text-sm mt-1" style={{ color: "rgba(201,184,150,0.7)" }}>
                  Get the most from your journey — paper, tools, and technique. Color your way.
                </p>
              </div>
            </div>
            <span className="colox-sans text-xs tracking-[0.2em] px-6 py-3 rounded-full whitespace-nowrap group-hover:brightness-110 transition-all" style={{ color: "#0a0a0f", background: "linear-gradient(135deg, #d4a254, #e8c87a)", fontWeight: 600 }}>
              READ THE GUIDE →
            </span>
          </Link>
        </AnimSection>
      </section>


      <section id="paperback" className="py-24 px-6 relative" style={{ background: "linear-gradient(to bottom, #0a0a0f, #100d18 50%, #0a0a0f)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(212,162,84,0.06) 0%, transparent 50%)" }} />
        <div className="relative z-10 max-w-3xl mx-auto">
          <AnimSection className="text-center mb-12">
            <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>INSPIYAH EDITION</p>
            <h2 className="colox-serif text-4xl sm:text-5xl font-light mb-4" style={{ color: "#f5efe6" }}>The Paperback</h2>
            <p className="colox-sans text-sm max-w-lg mx-auto" style={{ color: "rgba(201,184,150,0.5)" }}>
              One sacred edition, made with intention &mdash; held in your hands, colored
              at your own altar of stillness.
            </p>
          </AnimSection>

          <AnimSection>
            <div data-testid="paperback-card" className="colox-card rounded-2xl p-8 md:p-10 flex flex-col">
              <div className="text-center mb-6">
                <div
                  data-testid="paperback-cover-frame"
                  className="mx-auto mb-6 inline-block rounded-2xl"
                  style={{ background: "linear-gradient(160deg, #e8c87a, #d4a254)", padding: "16px", boxShadow: "0 18px 55px rgba(0,0,0,0.45), 0 0 40px rgba(212,162,84,0.28)" }}
                >
                  <img
                    data-testid="paperback-cover-image"
                    src={ASSETS.coverMockup}
                    alt="The Book of Colorz — INSPIYAH Edition cover"
                    className="rounded-lg"
                    style={{ maxHeight: "420px", width: "auto", display: "block" }}
                  />
                </div>
                <BookOpen className="w-9 h-9 mx-auto mb-3" style={{ color: "#d4a254" }} />
                <h3 className="colox-serif text-2xl font-semibold" style={{ color: "#e8c87a" }}>
                  The Book of Colorz &mdash; INSPIYAH Edition
                </h3>
                <p className="colox-serif text-sm italic mt-1" style={{ color: "rgba(201,184,150,0.5)" }}>
                  {PAPERBACK.meaning}
                </p>
              </div>

              <div className="text-center mb-8">
                <span data-testid="paperback-price" className="colox-serif text-5xl font-bold" style={{ color: "#f5efe6" }}>
                  {PAPERBACK.price}
                </span>
                <p className="colox-sans text-xs tracking-[0.25em] mt-3" style={{ color: "rgba(212,162,84,0.6)" }}>
                  IN DIVINE ALIGNMENT &middot; THE FREQUENCY OF 22
                </p>
                <p className="colox-sans text-xs mt-3 mb-1" style={{ color: "rgba(212,162,84,0.75)" }}>
                  22 Yahffirmations &nbsp;&middot;&nbsp; Seed 22: Creative Hands &nbsp;&middot;&nbsp; $22.22
                </p>
                <p className="colox-serif text-sm italic mt-2 max-w-md mx-auto leading-relaxed" style={{ color: "rgba(201,184,150,0.65)" }}>
                  $22.22 is resonance, not coincidence. Twenty-two Yahffirmations. Seed 22 &mdash;
                  the <em style={{ color: "#d4a254" }}>Creative Hands</em>. And a price that echoes
                  them both: a <strong style={{ color: "#e8c87a", fontStyle: "normal" }}>triad of success</strong>,
                  held in alignment. When your hands create in faith, you step into
                  <span style={{ color: "#d4a254" }}> YAH HEALTH WEALTH HAPPINESS</span> &mdash; The
                  Universal Pillars Of Success. Chosen with intention, never by chance.
                </p>
              </div>

              <div className="space-y-3 mb-8 max-w-md mx-auto w-full">
                {PAPERBACK.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#d4a254", opacity: 0.7 }} />
                    <span className="colox-sans text-sm leading-relaxed" style={{ color: "rgba(201,184,150,0.75)" }}>{item}</span>
                  </div>
                ))}
              </div>

              <button
                data-testid="paperback-checkout-btn"
                onClick={() => {
                  if (!billingEnabled) { scrollToSeedz(); return; }
                  setCheckoutError(""); setQuantity(1); setCheckoutStep("cart"); setShowCheckout(true);
                }}
                disabled={billingEnabled && checkoutLoading}
                className="colox-btn-gold w-full py-4 rounded-full text-sm"
                style={{ opacity: billingEnabled && checkoutLoading ? 0.6 : 1 }}
              >
                {billingEnabled ? (checkoutLoading ? "Preparing checkout..." : "GET YOUR 1ST INSPIYAH EDITION") : SOON_LABEL}
              </button>

              <div className="flex items-center justify-center gap-2 mt-4">
                <Truck className="w-3.5 h-3.5" style={{ color: "rgba(212,162,84,0.5)" }} />
                <span className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.4)" }}>
                  {billingEnabled
                    ? "Print-on-demand · ships to your door · secure checkout via Stripe"
                    : "Paperback arriving soon · plant your free Seedz meanwhile"}
                </span>
              </div>
            </div>
          </AnimSection>

          <AnimSection className="mt-8 text-center">
            <p className="colox-sans text-xs italic" style={{ color: "rgba(212,162,84,0.35)" }}>
              Written as a sacred scribe &mdash; The Keeper of the Fiyah, The Siren of Yah's Universe.
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ── CONTENT & AUDIENCE MICRO-NOTE ── */}
      <section className="pb-16 px-6" style={{ background: "#0a0a0f" }}>
        <AnimSection className="max-w-2xl mx-auto">
          <div className="colox-card rounded-xl p-6 text-center" data-testid="content-audience-note">
            <p className="colox-sans text-xs tracking-[0.22em] mb-2" style={{ color: "rgba(212,162,84,0.6)" }}>A NOTE FOR FAMILIES</p>
            <p className="colox-serif" style={{ color: "rgba(201,184,150,0.88)", fontSize: "1.05rem", lineHeight: 1.6 }}>
              An original artistic work that may portray the human form as sacred expression. Each edition notes its intended audience.
            </p>
            <Link to="/coloring-guide" data-testid="content-audience-link" className="colox-sans inline-block mt-4 text-xs tracking-[0.15em] hover:opacity-80 transition-opacity" style={{ color: "#e8c87a", textDecoration: "underline" }}>
              READ OUR CONTENT &amp; AUDIENCE NOTE →
            </Link>
          </div>
        </AnimSection>
      </section>

      {/* ── COLOR IT FORWARD (Buy 2 · Gift 1 — The Power of 3) ── */}
      <section id="color-it-forward" data-testid="color-it-forward-section" className="py-24 px-6 relative" style={{ background: "linear-gradient(to bottom, #0a0a0f, #100d18 50%, #0a0a0f)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 25%, rgba(212,162,84,0.06) 0%, transparent 55%)" }} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <AnimSection>
            <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>
              COLOR IT FORWARD &middot; THE POWER OF 3
            </p>
            <h2 className="colox-serif text-4xl sm:text-5xl font-light mb-6" style={{ color: "#f5efe6" }}>
              Two Held. One Released.
            </h2>
            <p className="colox-serif text-base sm:text-lg italic leading-relaxed mb-8 max-w-2xl mx-auto" style={{ color: "rgba(201,184,150,0.75)" }}>
              Some things are only whole in three. <span style={{ color: "#e8c87a" }}>She Is Her, She Is You, She Is Me.</span>{" "}
              When you receive your two copies, a third is Colored Forward &mdash; sent as Yah Spirit
              to a soul you may never meet. We know The Father, The Son, and now Yah Spirit, carried
              through your <em style={{ color: "#d4a254" }}>Creative Hands</em>.
            </p>
            <p className="colox-serif text-lg sm:text-xl tracking-wide mb-10" style={{ color: "#e8c87a" }}>
              THIS IS HOW COLORZ UNITE US.
            </p>
          </AnimSection>

          <AnimSection>
            <div className="grid sm:grid-cols-3 gap-6 mb-10">
              {[
                { n: "1", t: "Receive your two editions" },
                { n: "2", t: "A third is gifted in your name or anonymously" },
                { n: "3", t: "We place it with a vetted soul, group, or cause" },
              ].map((step) => (
                <div key={step.n} className="colox-card rounded-2xl p-6 flex flex-col items-center">
                  <div className="w-11 h-11 rounded-full flex items-center justify-center mb-4 colox-serif text-lg font-semibold" style={{ background: "linear-gradient(135deg, #d4a254, #e8c87a)", color: "#0a0a0f" }}>
                    {step.n}
                  </div>
                  <p className="colox-sans text-sm leading-relaxed" style={{ color: "rgba(201,184,150,0.75)" }}>{step.t}</p>
                </div>
              ))}
            </div>

            <div className="mb-8">
              <p className="colox-sans text-xs tracking-[0.25em] mb-1" style={{ color: "rgba(212,162,84,0.55)" }}>TRIADS COMPLETED</p>
              <p data-testid="cif-counter" className="colox-serif text-4xl font-bold" style={{ color: "#f5efe6" }}>0</p>
            </div>

            <a href="#paperback">
              <button data-testid="cif-cta-btn" className="colox-btn-gold px-10 py-4 rounded-full text-sm inline-flex items-center gap-2">
                <Gift className="w-4 h-4" /> COLOR IT FORWARD &mdash; GIFT THE 3RD
              </button>
            </a>
            <p className="colox-serif text-sm italic mt-6" style={{ color: "rgba(201,184,150,0.5)" }}>
              "Complete the Triad &mdash; two for you, one for Yah's Spirit to carry."
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ── FREE SEEDZ (front door / lead magnet) ── */}
      <section id="seedz" className="py-24 px-6 relative overflow-hidden" style={{ background: "#0a0a0f" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(212,162,84,0.05), transparent 60%)" }} />
        <div className="relative z-10 max-w-3xl mx-auto">
          <AnimSection>
            <div className="colox-card rounded-2xl p-8 md:p-12 text-center">
              <Leaf className="w-8 h-8 mx-auto mb-6 animate-soft-pulse" style={{ color: "#d4a254" }} />
              <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>
                A GIFT TO INSPIYAH
              </p>
              <h2 className="colox-serif text-3xl sm:text-4xl font-light mb-6" style={{ color: "#f5efe6" }}>
                "BOOK OF COLORZ &mdash; SEEDZ"
              </h2>
              <p className="colox-serif text-base sm:text-lg italic leading-relaxed mb-6" style={{ color: "#c9b896" }}>
                Thank you for taking your 1st step towards coloring for
                <span style={{ color: "#e8c87a" }}> YAH HEALTH WEALTH HAPPINESS</span>, The
                Universal Pillars Of Success. Where colorz unite us!
              </p>
              <p className="colox-sans text-sm leading-relaxed mb-8" style={{ color: "rgba(201,184,150,0.7)" }}>
                Receive your free <strong style={{ color: "#e8c87a" }}>“BOOK OF COLORZ &mdash; SEEDZ”</strong> below
                &mdash; <strong>7 of our unique coloring experiences</strong> to keep, and to send to your
                friends &amp; family as a gift to INSPIYAH. Plant a seed today; watch it bloom.
              </p>
              {!seedzDone ? (
                <form onSubmit={handleSeedzSubmit} data-testid="seedz-form" className="max-w-md mx-auto flex flex-col gap-3">
                  <input
                    type="text"
                    data-testid="seedz-name-input"
                    value={seedzName}
                    onChange={(e) => setSeedzName(e.target.value)}
                    placeholder="Your name"
                    className="colox-sans w-full px-5 py-3 rounded-full text-sm outline-none"
                    style={{ background: "rgba(212,162,84,0.06)", border: "1px solid rgba(212,162,84,0.25)", color: "#f5efe6" }}
                  />
                  <input
                    type="email"
                    data-testid="seedz-email-input"
                    value={seedzEmail}
                    onChange={(e) => setSeedzEmail(e.target.value)}
                    placeholder="Your email"
                    className="colox-sans w-full px-5 py-3 rounded-full text-sm outline-none"
                    style={{ background: "rgba(212,162,84,0.06)", border: "1px solid rgba(212,162,84,0.25)", color: "#f5efe6" }}
                  />
                  {seedzError && (
                    <p data-testid="seedz-error" className="colox-sans text-xs" style={{ color: "#e0806a" }}>{seedzError}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="seedz-submit-btn"
                    disabled={seedzSubmitting}
                    className="colox-btn-gold px-8 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2"
                    style={{ opacity: seedzSubmitting ? 0.6 : 1 }}
                  >
                    <Download className="w-4 h-4" />
                    {seedzSubmitting ? "SENDING..." : "SEND ME THE FREE SEEDZ"}
                  </button>
                  <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.4)" }}>
                    We'll email your Seedz and the occasional INSPIYAHtion. Unsubscribe anytime.
                  </p>
                </form>
              ) : (
                <div data-testid="seedz-success" className="max-w-md mx-auto">
                  <p className="colox-serif text-lg italic mb-3" style={{ color: "#e8c87a" }}>
                    Your Seedz are on the way &mdash; check your email.
                  </p>
                  <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.55)" }}>
                    Open the email from us and tap <b style={{ color: "#e8c87a" }}>Download Your Free Seedz</b> &mdash; the secure link is good for 30 days. Don&rsquo;t see it? Check spam / promotions.
                  </p>
                </div>
              )}
              <p className="colox-serif text-sm italic mt-8" style={{ color: "rgba(212,162,84,0.5)" }}>
                "&amp; you will know the truth &amp; the truth will set you free."
              </p>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ── ROOTS INTERLUDE ── */}
      <section className="relative py-32 overflow-hidden">
        <img src={ASSETS.cathedral} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.12, filter: "saturate(0.5) brightness(0.4)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #0a0a0f, rgba(10,10,15,0.4), #0a0a0f)" }} />
        <div className="relative z-10 text-center px-6">
          <AnimSection>
            <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>FOR THE WOMAN WHO WALKS IN FAITH</p>
            <h2 className="colox-serif text-3xl sm:text-5xl font-light italic mb-6" style={{ color: "#f5efe6" }}>
              "You don't leave your roots.<br />You discover new branches."
            </h2>
            <p className="colox-sans text-sm max-w-md mx-auto" style={{ color: "rgba(201,184,150,0.5)" }}>
              This book is for the believer who is ready to explore the beauty already
              present within her faith &mdash; the divine feminine energy that scripture has
              always held.
            </p>
          </AnimSection>
        </div>
      </section>

      {/* ── A NOTE FROM THE CREATOR ── */}
      <section id="creator-note" data-testid="creator-note-section" className="py-24 px-6 relative" style={{ background: "#0a0a0f" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 20%, rgba(212,162,84,0.05) 0%, transparent 55%)" }} />
        <div className="relative z-10 max-w-2xl mx-auto">
          <AnimSection className="text-center mb-10">
            <Flame className="w-7 h-7 mx-auto mb-5 animate-soft-pulse" style={{ color: "#d4a254" }} />
            <p className="colox-sans text-xs tracking-[0.3em] mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>A NOTE FROM THE CREATOR</p>
            <h2 className="colox-serif text-3xl sm:text-4xl font-light" style={{ color: "#f5efe6" }}>
              Welcome To The World Of INSPIYAH!
            </h2>
          </AnimSection>

          <AnimSection>
            <div className="space-y-5 colox-serif text-base sm:text-lg leading-relaxed" style={{ color: "rgba(201,184,150,0.78)" }}>
              <p>
                To my Yah lovers, thank you for welcoming this A.R.T. Energi collectible into
                your frequency and taking your 1st step towards coloring for
                <span style={{ color: "#d4a254" }}> YAH HEALTH WEALTH HAPPINESS</span>, The
                Universal Pillars Of Success.
              </p>
              <p>
                Every INSPIYAHED Edition begins long before its publication. This coloring
                experience &mdash; <em style={{ color: "#e8c87a" }}>&ldquo;WHERE COLORZ UNITE US&rdquo;</em> &mdash;
                carries two histories: it starts as a word, an experience, a download, a thought,
                an observation, a visual perspective, or a healing modality &mdash; gradually
                taking shape through prayer, quiet meditation, research, music, reflection,
                revision, and time. As the Yahffirmation forms, the generational history expands
                forward until the moment these ideas are entrusted to the Yah-loving audience.
              </p>
              <p>
                While this edition marks the official publication of the work, the ideas behind
                this expression of A.R.T. Energi often span months, years, or centuries of
                evolution. As a Temple of Yah, my expressive A.R.T. Energi continues to transmute
                and &ldquo;free Yah Mind&rdquo; &mdash; even after these pages are printed, where
                you pick up the torch to be &ldquo;INSPIYAHED.&rdquo;
              </p>
              <p>
                Yes, it&rsquo;s time to let Yah Spirit sear Its Word on Yah Soul. I AM so humbly
                excited that my passion to boast in the name of Yah resonates with you. As my
                personal Inspiyah Journey continues, additional creative works, companion
                materials, limited future editions, collector releases, and ongoing writings will
                be revealed as each project reaches completion.
              </p>
              <p>
                Thank you for becoming part of The &ldquo;Book Of Colorz&rdquo; journey. To explore
                the complete collection and receive notice of advance releases, collector editions,
                and further announcements, join the community &mdash; plant your free Seedz above and
                step deeper into the World of INSPIYAH.
              </p>
            </div>

            <div className="colox-divider w-40 mx-auto my-8" />
            <div className="text-center">
              <p className="colox-serif text-lg italic" style={{ color: "#d4a254" }}>Yours Truly,</p>
              <p className="colox-serif text-xl font-semibold mt-1" style={{ color: "#e8c87a" }}>Keeper Of The Fiyah</p>
              <p className="colox-sans text-xs tracking-[0.2em] mt-1" style={{ color: "rgba(201,184,150,0.45)" }}>
                AKA SIREN OF YAH&rsquo;S UNIVERSE
              </p>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ── OUR STORY ── */}
      <section id="our-story" className="py-24 px-6 relative" style={{ background: "linear-gradient(to bottom, #0a0a0f, #0f0c14, #0a0a0f)" }}>
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 70% 50%, rgba(212,162,84,0.04) 0%, transparent 50%)" }} />
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimSection>
            <div className="colox-card rounded-2xl p-8 md:p-12">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="flex-shrink-0">
                  <div className="w-36 h-36 rounded-full overflow-hidden" data-testid="creator-seal-badge" style={{ boxShadow: "0 0 26px rgba(212,162,84,0.25)" }}>
                    <img src="/assets/creatorecho-seal.png" alt="CreatorEcho Seal — Keeper of the Fiyah · A CreatorEcho Work" className="w-full h-full object-cover" />
                  </div>
                </div>
                <div>
                  <p className="colox-sans text-xs tracking-[0.2em] mb-3" style={{ color: "rgba(212,162,84,0.5)" }}>FROM THE CREATOR</p>
                  <blockquote className="colox-serif text-lg italic leading-relaxed mb-4" style={{ color: "#c9b896" }}>
                    "These pages were curated with intention &mdash; each design, each
                    affirmation, each sacred pattern is meant to orient your spirit toward
                    divine connection. When you color, you meditate. When you meditate, you
                    connect. This is YAHFFIRMATIONS in action."
                  </blockquote>
                  <p className="colox-serif font-semibold" style={{ color: "#d4a254" }}>Keeper Of The Fiyah</p>
                  <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.4)" }}>Creator, INSPIYAH | A.R.T. Energi</p>
                  <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.3)" }}>Authenticity | Reliability | Truth</p>
                </div>
              </div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 50%, rgba(212,162,84,0.08), transparent 60%), linear-gradient(to bottom, rgba(10,10,15,0.8), #0a0a0f)" }} />
        <AnimSection className="relative z-10 text-center max-w-2xl mx-auto">
          <Flame className="w-8 h-8 mx-auto mb-6 animate-soft-pulse" style={{ color: "#d4a254" }} />
          <h2 className="colox-serif text-4xl sm:text-5xl font-light mb-4" style={{ color: "#f5efe6" }}>Begin Your Sacred Journey</h2>
          <p className="colox-sans text-sm mb-8 leading-relaxed" style={{ color: "rgba(201,184,150,0.6)" }}>
            The light is already within you. This book simply helps you see it in color.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={billingEnabled ? "#paperback" : "#seedz"}>
              <button data-testid="final-cta-btn" className="colox-btn-gold px-10 py-4 rounded-full text-sm">{billingEnabled ? "GET YOUR 1ST INSPIYAH EDITION" : "COMING SOON — GET THE FREE SEEDZ"}</button>
            </a>
            <a href="#seedz">
              <button className="colox-btn-outline px-10 py-4 rounded-full text-sm">FREE SEEDZ</button>
            </a>
          </div>
        </AnimSection>
      </section>

      {/* ── FOOTER ── */}
      <footer data-testid="colorz-footer" className="py-12 px-6" style={{ background: "#07070a", borderTop: "1px solid rgba(212,162,84,0.08)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-4 h-4" style={{ color: "#d4a254" }} />
                <span className="colox-serif text-lg font-semibold" style={{ color: "#e8c87a" }}>THE BOOK OF COLORZ</span>
              </div>
              <p className="colox-sans text-xs leading-relaxed" style={{ color: "rgba(201,184,150,0.4)" }}>
                A sacred coloring experience by Keeper Of The Fiyah. Part of the INSPIYAH Ecosystem.
              </p>
            </div>
            <div>
              <p className="colox-sans text-xs tracking-widest mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>EXPLORE</p>
              <div className="space-y-2">
                {[
                  { label: "The Book", href: "#the-book" },
                  { label: "The Paperback", href: "#paperback" },
                  { label: "Free Seedz", href: "#seedz" },
                  { label: "Our Story", href: "#our-story" },
                ].map((link) => (
                  <a key={link.label} href={link.href} className="block colox-sans text-xs hover:opacity-100 transition-opacity" style={{ color: "rgba(201,184,150,0.4)" }}>
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <p className="colox-sans text-xs tracking-widest mb-4" style={{ color: "rgba(212,162,84,0.5)" }}>SPIRIT</p>
              <p className="colox-serif italic text-sm mb-2" style={{ color: "rgba(201,184,150,0.4)" }}>"Let Yah Spirit Sear Its Word on Yah Soul"</p>
              <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.3)" }}>YAH HEALTH WEALTH HAPPINESS</p>
              <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.3)" }}>The Universal Pillars Of Success</p>
            </div>
          </div>
          <div className="colox-divider w-full mb-6" />
          <nav className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 mb-5 colox-sans text-xs" aria-label="Legal" data-testid="boc-legal-nav">
            <a href="/book-of-colorz/privacy-policy" style={{ color: "#d4a254" }} data-testid="boc-legal-privacy">Privacy Policy</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <a href="/book-of-colorz/data-processing-addendum" style={{ color: "#d4a254" }} data-testid="boc-legal-dpa">DPA</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <a href="/book-of-colorz/terms-of-service" style={{ color: "#d4a254" }} data-testid="boc-legal-terms">Terms of Service</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <a href="/book-of-colorz/ai-usage-policy" style={{ color: "#d4a254" }} data-testid="boc-legal-ai">AI Usage Policy</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <a href="/book-of-colorz/disclaimer" style={{ color: "#d4a254" }} data-testid="boc-legal-disclaimer">Disclaimer</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <a href="/faq" style={{ color: "#d4a254" }} data-testid="boc-legal-faq">FAQ</a>
            <span style={{ color: "rgba(201,184,150,0.3)" }}>&bull;</span>
            <Link to="/coloring-guide" style={{ color: "#d4a254" }} data-testid="footer-content-audience-link">Content &amp; Audience</Link>
          </nav>
          <div className="text-center">
            <p className="colox-sans text-xs" style={{ color: "rgba(201,184,150,0.3)" }}>&copy; {new Date().getFullYear()} WOI. All rights reserved.</p>
            <p className="colox-sans text-xs mt-1" style={{ color: "#d4a254" }}>A.R.T. Energi (Authenticity | Reliability | Truth) &mdash; Triad Of Excellence</p>
            <p className="colox-sans text-xs mt-2" style={{ color: "rgba(201,184,150,0.35)" }}>YAHFFIRMATIONS BY KOTF888</p>
            <p className="colox-sans text-xs mt-1" style={{ color: "rgba(201,184,150,0.35)" }}>184 INSPIYAH · support@bookofcolorz.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BookOfColorz;
